import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
    addFolderLink,
    FolderLink,
    updateFolderLink,
} from '../../../services/api/folderService';

interface LinkDialogProps {
    folderId: string;
    link?: FolderLink;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSaved: () => void;
}

const withScheme = (address: string) =>
    /^[a-z][a-z\d+-]*:(?!\d)/i.test(address) ? address : `https://${address}`;

const isHttpUrl = (value: string) => {
    try {
        const { protocol } = new URL(value);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

export const LinkDialog = ({
    folderId,
    link,
    open,
    onOpenChange,
    onSaved,
}: LinkDialogProps) => {
    const { t } = useTranslation();
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTitle(link?.title ?? '');
        setUrl(link?.url ?? '');
        setError(null);
    }, [open, link]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const input = { title: title.trim(), url: withScheme(url.trim()) };
        if (!isHttpUrl(input.url)) {
            setError(t('folders.links.urlInvalid'));
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (link) {
                await updateFolderLink(folderId, link._id, input);
            } else {
                await addFolderLink(folderId, input);
            }
            onOpenChange(false);
            onSaved();
        } catch {
            setError(t('folders.links.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {link
                            ? t('folders.links.editTitle')
                            : t('folders.links.addTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('folders.links.description')}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="folder-link-title">
                            {t('folders.links.titleLabel')}
                        </Label>
                        <Input
                            id="folder-link-title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={120}
                            autoFocus
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="folder-link-url">
                            {t('folders.links.urlLabel')}
                            <span className="ml-0.5 text-destructive">*</span>
                        </Label>
                        <Input
                            id="folder-link-url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="www.example.com"
                            inputMode="url"
                            aria-required="true"
                        />
                    </div>
                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            {t('folders.create.cancel')}
                        </Button>
                        <Button type="submit" disabled={saving}>
                            {link
                                ? t('folders.create.save')
                                : t('folders.links.add')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
